import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PaymentService from '../../../services/paymentService';
import PaymentFormService from '../../../services/paymentFormService';
import PatientService from '../../../services/patientService';
import UserService from '../../../services/userService';
import OrganizationService from '../../../services/organizationService';
import ProfileService from '../../../services/profileService';
import Button from '../../common/Button';
import ToastContainer from '../../common/ToastContainer';
import PatientSectionHeader from '../common/PatientSectionHeader';
import RefundModal from './RefundModal';
import './PatientPayments.css';
import { formatDateTimeInTimezone } from '../../../utils/dateUtils';
import { useOrganizationTimezone } from '../../../hooks/useOrganizationTimezone';
import { printPaymentReceipt } from '../../../utils/paymentReceiptPrintUtils';
import { useToast } from '../../../hooks/useToast';

// Use Mexican Peso formatting consistently
const formatCurrency = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n || 0));

const PatientPayments = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { timezone } = useOrganizationTimezone();
  const { toasts, removeToast, success: showSuccess } = useToast();
  const processedToastKeyRef = useRef(null);

  const [patient, setPatient] = useState(null);
  const [payments, setPayments] = useState([]);
  const [forms, setForms] = useState([]);
  const [users, setUsers] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!auth.user?.access_token) return;
      setLoading(true); setError(null);
      try {
        const userId = auth.user?.profile?.sub || auth.user?.sub;
        const [p, pay, pf, usr, org, doc] = await Promise.all([
          PatientService.getPatientById(patientId, auth.user.access_token),
          PaymentService.listByPatient(patientId, auth.user.access_token),
          PaymentFormService.getPaymentForms(auth.user.access_token),
          UserService.getAllUsers(auth.user.access_token),
          userId ? OrganizationService.getCurrentUserOrganization(userId, auth.user.access_token) : Promise.resolve(null),
          userId ? ProfileService.getUserProfile(userId, auth.user.access_token).catch(() => null) : Promise.resolve(null)
        ]);
        setPatient(p);
        const list = Array.isArray(pay.data) ? pay.data : (pay.payments || []);
        setPayments(list);
        setForms(pf.paymentForms || pf.data || []);
        setUsers(usr.users || usr.data || []);
        setOrganization(org);
        setDoctor(doc);
      } catch (e) {
        console.error(e);
        setError('Error al cargar pagos');
      } finally { setLoading(false); }
    };
    load();
  }, [patientId, auth.user?.access_token, auth.user?.profile?.sub, auth.user?.sub]);

  const totals = useMemo(() => {
    const total = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
    const totalRefunds = payments.reduce((sum, p) => sum + Number(p.refundAmount || 0), 0);
    const netTotal = total - totalRefunds;
    return { total, totalRefunds, netTotal };
  }, [payments]);

  const handleRefund = (payment) => {
    const maxRefundAmount = payment.amount - (payment.refundAmount || 0);
    
    if (maxRefundAmount <= 0) {
      alert('Este pago ya ha sido reembolsado completamente.');
      return;
    }

    setSelectedPayment(payment);
    setShowRefundModal(true);
  };

  const handleConfirmRefund = async (refundAmount, refundNote) => {
    try {
      await PaymentService.refundPayment(selectedPayment.id, refundAmount, refundNote, auth.user.access_token);
      
      // Reload payments
      const paymentResponse = await PaymentService.listByPatient(patientId, auth.user.access_token);
      const list = Array.isArray(paymentResponse.data) ? paymentResponse.data : (paymentResponse.payments || []);
      setPayments(list);
      
      setShowRefundModal(false);
      setSelectedPayment(null);
    } catch (e) {
      console.error(e);
      throw new Error(e.message || 'Error al procesar el reembolso');
    }
  };

  const handleCloseRefundModal = () => {
    setShowRefundModal(false);
    setSelectedPayment(null);
  };

  const handlePrintReceipt = useCallback((payment) => {
    printPaymentReceipt(
      payment,
      patient,
      organization?.logoViewUrl,
      organization,
      doctor,
      forms,
      users
    );
  }, [doctor, forms, organization, patient, users]);

  useEffect(() => {
    if (!location.state?.message) return;
    const toastKey = location.state.toastKey;

    if (toastKey && sessionStorage.getItem(`payment-toast:${toastKey}`)) {
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    if (toastKey && processedToastKeyRef.current === toastKey) return;

    if (toastKey) {
      processedToastKeyRef.current = toastKey;
      sessionStorage.setItem(`payment-toast:${toastKey}`, 'shown');
    }

    showSuccess(location.state.message, 10000, location.state.payment ? {
      actionLabel: 'Imprimir recibo',
      onAction: () => handlePrintReceipt(location.state.payment)
    } : undefined);
    navigate(location.pathname, { replace: true, state: {} });
  }, [handlePrintReceipt, location.pathname, location.state, navigate, showSuccess]);

  const getAllocationLabel = (allocationUserId) => {
    if (!allocationUserId) return null;
    if (allocationUserId === 'GENERAL_SALES') return 'Venta General';
    const user = users.find(u => u.id === allocationUserId);
    return user ? (user.name || user.firstName) : 'Usuario desconocido';
  };

  return (
    <div className="patient-payments-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
        sectionTitle="Pagos"
        actionButton={
          <Button 
            variant="primary" 
            icon="fas fa-plus"
            onClick={() => navigate(`/payments/new?patientId=${patientId}`)}
          >
            Registrar Pago
          </Button>
        }
      />

      {error && <div className="pp-error">{error}</div>}
      {loading && (
        <div className="pp-loading"><div className="loading-spinner"></div></div>
      )}

      {!loading && !error && (
        <div className="pp-content">
          <div className="pp-summary">
            <div className="pp-summary-card">
              <div className="pp-summary-label">Total Pagado</div>
              <div className="pp-summary-value">{formatCurrency(totals.total)}</div>
            </div>
            {totals.totalRefunds > 0 && (
              <div className="pp-summary-card">
                <div className="pp-summary-label">Total Reembolsado</div>
                <div className="pp-summary-value" style={{ color: '#e74c3c' }}>-{formatCurrency(totals.totalRefunds)}</div>
              </div>
            )}
            <div className="pp-summary-card">
              <div className="pp-summary-label">Total Neto</div>
              <div className="pp-summary-value" style={{ fontWeight: 'bold' }}>{formatCurrency(totals.netTotal)}</div>
            </div>
          </div>

          <div className="pp-list-section">
            <h2>Historial</h2>
            {payments.length === 0 ? (
              <div className="pp-empty">Sin pagos registrados</div>
            ) : (
              <ul className="pp-list">
                {payments.map(p => {
                  const paymentAmount = Number(p.amount || 0);
                  const refundAmount = Number(p.refundAmount || 0);
                  const netAmount = paymentAmount - refundAmount;
                  const isFullyRefunded = refundAmount >= paymentAmount && refundAmount > 0;
                  const hasPartialRefund = refundAmount > 0 && !isFullyRefunded;
                  const paymentConcept = p.concept?.trim() || 'Pago recibido';
                  
                  return (
                    <li key={p.id} className={`pp-item ${isFullyRefunded ? 'fully-refunded' : ''}`}>
                      <div className="pp-item-content">
                        <div className="pp-amount-section">
                          <div className="pp-amount-header">
                            <span className="pp-amount">{formatCurrency(p.amount)}</span>
                            <span className="pp-concept">{paymentConcept}</span>
                            {p.allocationUserId && (
                              <span className="pp-allocation">
                                <i className="fas fa-user-tag"></i> {getAllocationLabel(p.allocationUserId)}
                              </span>
                            )}
                          </div>
                          {hasPartialRefund && (
                            <div className="pp-refund-info">
                              <span className="pp-refund-amount">Reembolsado: {formatCurrency(p.refundAmount)}</span>
                              <span className="pp-net-amount">Neto: {formatCurrency(netAmount)}</span>
                            </div>
                          )}
                          {isFullyRefunded && (
                            <div className="pp-refund-info">
                              <span className="pp-refunded-badge">Reembolsado Completo</span>
                            </div>
                          )}
                        </div>
                        <div className="pp-meta">
                          <span className="pp-date"><i className="far fa-calendar"></i> {formatDateTimeInTimezone(p.paymentDate || p.createdAt, timezone).date}</span>
                          <span className="pp-form"><i className="far fa-credit-card"></i> {forms.find(f => f.id === p.paymentFormId)?.name || 'Forma de pago'}</span>
                        </div>
                        <div className="pp-notes-section">
                          {p.note && <div className="pp-note"><i className="fas fa-sticky-note"></i> {p.note}</div>}
                          {p.refunds && p.refunds.length > 0 && (
                            <div className="pp-refunds-history">
                              <div className="pp-refunds-title">
                                <i className="fas fa-undo"></i> Historial de reembolsos:
                              </div>
                              {p.refunds.map((refund, index) => (
                                <div key={refund.id || index} className="pp-refund-entry">
                                  <span className="pp-refund-amount-small">{formatCurrency(refund.amount)}</span>
                                  {refund.note && <span className="pp-refund-note-small"> - {refund.note}</span>}
                                  <span className="pp-refund-date"> ({formatDateTimeInTimezone(refund.date, timezone).date})</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Fallback for old refunds without history */}
                          {p.refundNote && (!p.refunds || p.refunds.length === 0) && (
                            <div className="pp-refund-note">
                              <i className="fas fa-undo"></i> Motivo del reembolso: {p.refundNote}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="pp-item-actions">
                        <button
                          className="pp-action-btn print-btn"
                          onClick={() => handlePrintReceipt(p)}
                          title="Imprimir recibo"
                        >
                          <i className="fas fa-print"></i>
                        </button>
                        {!isFullyRefunded && (
                          <button 
                            className="pp-action-btn refund-btn"
                            onClick={() => handleRefund(p)}
                            title="Reembolsar pago"
                          >
                            <i className="fas fa-undo"></i>
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {showRefundModal && selectedPayment && (
        <RefundModal
          payment={selectedPayment}
          onClose={handleCloseRefundModal}
          onConfirm={handleConfirmRefund}
        />
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default PatientPayments;
